import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiDelete, apiGet, apiPatch, apiPost } from '@/lib/api';

export type ChannelKind = 'WEB_CHAT' | 'TELEGRAM' | 'FACEBOOK' | 'INSTAGRAM' | 'EMAIL' | 'VIBER' | 'SMS';
export type ConversationKind = ChannelKind | 'WHATSAPP';
export type ConversationStatus = 'OPEN' | 'ASSIGNED' | 'RESOLVED';

export const KIND_LABELS: Record<ConversationKind, string> = {
  WEB_CHAT: 'Website chat',
  TELEGRAM: 'Telegram',
  FACEBOOK: 'Messenger',
  INSTAGRAM: 'Instagram',
  EMAIL: 'Email',
  VIBER: 'Viber',
  SMS: 'SMS',
  WHATSAPP: 'WhatsApp',
};

/** Brand-ish colours, so a glance at the list says which messenger it is. */
export const KIND_COLOURS: Record<ConversationKind, string> = {
  WEB_CHAT: '#2563eb',
  TELEGRAM: '#229ED9',
  FACEBOOK: '#0866FF',
  INSTAGRAM: '#E1306C',
  EMAIL: '#f59e0b',
  VIBER: '#7360F2',
  SMS: '#475569',
  WHATSAPP: '#25D366',
};

type LeadRef = { id: string; number: string; firstName: string; lastName: string | null; isUnsorted: boolean; status: string };
type ContactRef = { id: string; firstName: string; lastName: string | null; companyId: string | null };

export type ConversationRow = {
  id: string;
  source: 'inbox' | 'whatsapp';
  kind: ConversationKind;
  channel: { id: string; name: string };
  name: string;
  avatarUrl: string | null;
  preview: string | null;
  lastMessageAt: string | null;
  unreadCount: number;
  status: ConversationStatus;
  assignedTo: { id: string; name: string } | null;
  lead: LeadRef | null;
  contact: ContactRef | null;
  botActive: boolean;
  /** When a person's reply is due, against the channel's target. */
  replyDueAt: string | null;
  overdue: boolean;
};

export type InboxMessage = {
  id: string;
  direction: 'INBOUND' | 'OUTBOUND';
  body: string | null;
  attachments: { type: string; url?: string; name?: string }[] | null;
  buttons: string[];
  isInternalNote: boolean;
  isBot: boolean;
  status: string;
  errorMessage: string | null;
  createdAt: string;
  sentBy: { id: string; name: string } | null;
};

export type ConversationDetail = {
  id: string;
  externalId: string;
  displayName: string | null;
  email: string | null;
  phone: string | null;
  avatarUrl: string | null;
  status: ConversationStatus;
  context: { pageUrl?: string; referrer?: string; utm?: Record<string, string>; username?: string; subject?: string } | null;
  botId: string | null;
  createdAt: string;
  channel: { id: string; kind: ChannelKind; name: string };
  assignedTo: { id: string; name: string } | null;
  lead: LeadRef | null;
  contact: ContactRef | null;
  bot: { id: string; name: string } | null;
  messages: InboxMessage[];
};

export type Channel = {
  id: string;
  kind: ChannelKind;
  name: string;
  publicKey: string;
  settings: Record<string, unknown> | null;
  autoCreateLead: boolean;
  replyWithinMinutes: number | null;
  isActive: boolean;
  lastError: string | null;
  connected: boolean;
  mailbox?: {
    address: string;
    fromName?: string;
    imapHost: string;
    imapPort: number;
    smtpHost: string;
    smtpPort: number;
    username: string;
  };
  hasAppSecret: boolean;
  conversationCount: number;
  defaultAssignee: { id: string; name: string } | null;
  salesbot: { id: string; name: string; status: string } | null;
  webhookUrl?: string;
  verifyToken?: string;
  embedCode?: string;
};

export type BotStep =
  | { id: string; type: 'message'; text: string; next?: string | null }
  | { id: string; type: 'question'; text: string; field: string; next?: string | null }
  | { id: string; type: 'buttons'; text: string; field?: string | null; options: { label: string; next?: string | null }[] }
  | {
      id: string;
      type: 'condition';
      field: string;
      operator: 'contains' | 'equals' | 'is_empty' | 'is_not_empty';
      value?: string | null;
      then?: string | null;
      else?: string | null;
    }
  | { id: string; type: 'action'; action: 'create_lead' | 'add_tag' | 'assign' | 'notify'; config?: Record<string, string>; next?: string | null }
  | { id: string; type: 'handoff'; text?: string | null }
  | { id: string; type: 'end'; text?: string | null };

export type Salesbot = {
  id: string;
  name: string;
  description: string | null;
  status: 'DRAFT' | 'ACTIVE' | 'PAUSED';
  trigger: 'new_conversation' | 'keyword' | 'manual';
  keywords: string[];
  steps: BotStep[];
  runCount: number;
  updatedAt: string;
  _count?: { channels: number; whatsappAccounts: number };
};

// ── Conversations ────────────────────────────────────────────────────────────

export function useConversations(params: { status?: string; kind?: string; assigned?: string; q?: string }) {
  return useQuery({
    queryKey: ['inbox', 'list', params],
    queryFn: () =>
      apiGet<ConversationRow[]>('/inbox/conversations', {
        params: Object.fromEntries(Object.entries(params).filter(([, value]) => value)),
      }),
    refetchInterval: 8_000,
    placeholderData: (previous) => previous,
  });
}

export function useInboxUnread(enabled = true) {
  return useQuery({
    queryKey: ['inbox', 'unread'],
    queryFn: () => apiGet<{ total: number }>('/inbox/unread'),
    refetchInterval: 30_000,
    enabled,
  });
}

export function useConversation(id: string | undefined) {
  return useQuery({
    queryKey: ['inbox', 'conversation', id],
    queryFn: () => apiGet<ConversationDetail>(`/inbox/conversations/${id}`),
    enabled: Boolean(id),
    refetchInterval: 5_000,
  });
}

export function useReply(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: { body: string; isInternalNote?: boolean }) => apiPost(`/inbox/conversations/${id}/messages`, body),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['inbox'] }),
  });
}

export function useUpdateConversation(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: { status?: ConversationStatus; assignedToId?: string | null }) =>
      apiPatch(`/inbox/conversations/${id}`, body),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['inbox'] }),
  });
}

export function useConversationLead(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => apiPost<{ leadId: string }>(`/inbox/conversations/${id}/lead`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['inbox'] });
      void queryClient.invalidateQueries({ queryKey: ['leads'] });
    },
  });
}

export function useConversationBot(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (salesbotId: string | null) => apiPost(`/inbox/conversations/${id}/bot`, { salesbotId }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['inbox'] }),
  });
}

// ── Channels ─────────────────────────────────────────────────────────────────

export function useChannels(enabled = true) {
  return useQuery({ queryKey: ['inbox', 'channels'], queryFn: () => apiGet<Channel[]>('/inbox/channels'), enabled });
}

export function useSaveChannel() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id?: string } & Record<string, unknown>) =>
      id ? apiPatch<Channel>(`/inbox/channels/${id}`, body) : apiPost<Channel>('/inbox/channels', body),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['inbox', 'channels'] }),
  });
}

export function useDeleteChannel() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiDelete(`/inbox/channels/${id}`),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['inbox', 'channels'] }),
  });
}

// ── Salesbots ────────────────────────────────────────────────────────────────

export function useSalesbots(enabled = true) {
  return useQuery({ queryKey: ['salesbots'], queryFn: () => apiGet<Salesbot[]>('/salesbots'), enabled });
}

export function useSaveSalesbot() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: Partial<Salesbot> & { id?: string }) =>
      id ? apiPatch<Salesbot>(`/salesbots/${id}`, body) : apiPost<Salesbot>('/salesbots', body),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['salesbots'] }),
  });
}

export function useSalesbotStatus() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, status }: { id: string; status: Salesbot['status'] }) =>
      apiPatch(`/salesbots/${id}/status`, { status }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['salesbots'] }),
  });
}

export function useDeleteSalesbot() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiDelete(`/salesbots/${id}`),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['salesbots'] }),
  });
}

/** The reply-time targets on offer, in minutes. */
export const REPLY_TARGETS: [number, string][] = [
  [5, '5 minutes'],
  [15, '15 minutes'],
  [30, '30 minutes'],
  [60, '1 hour'],
  [240, '4 hours'],
  [1440, '1 day'],
];
